import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
function ToolCard({
  icon,
  title,
  desc,
  delay = 0
}: {
  icon: string;
  title: string;
  desc: string;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay
  }} className="bg-white p-6 rounded-xl shadow-sm border border-warm-gray" data-id="element-231">
      <div className="text-3xl mb-3" data-id="element-232">{icon}</div>
      <h4 className="font-bold text-lg mb-2 text-charcoal" data-id="element-233">{title}</h4>
      <p className="text-charcoal/70 text-sm leading-relaxed" data-id="element-234">{desc}</p>
    </motion.div>;
}
function FeaturedTool({
  icon,
  title,
  desc,
  tagline,
  imageSrc,
  imageAlt,
  imageLeft = false
}: {
  icon: string;
  title: string;
  desc: string;
  tagline: string;
  imageSrc: string;
  imageAlt: string;
  imageLeft?: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="bg-white rounded-2xl shadow-sm border border-warm-gray overflow-hidden" data-id="element-235">
      <div className="grid md:grid-cols-2" data-id="element-236">
        <div className={`relative ${imageLeft ? 'order-2 md:order-1' : 'order-2'}`} data-id="element-237">
          <img src={imageSrc} alt={imageAlt} className="w-full h-full object-cover" data-id="element-238" />
        </div>
        <div className={`p-8 md:p-10 flex flex-col justify-center ${imageLeft ? 'order-1 md:order-2' : 'order-1'}`} data-id="element-239">
          <div className="text-3xl mb-3" data-id="element-240">{icon}</div>
          <h4 className="font-bold text-xl md:text-2xl mb-3 text-charcoal" data-id="element-241">
            {title}
          </h4>
          <p className="text-charcoal/70 leading-relaxed mb-4" data-id="element-242">{desc}</p>
          <p className="text-gold font-semibold text-sm font-serif italic" data-id="element-243">
            {tagline}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function ValueStackSection() {
  return <section className="py-20 md:py-28 px-4 bg-cream watermark-bg" data-id="element-244">
      <div className="max-w-4xl mx-auto" data-id="element-245">
        {/* ── Section Header ── */}
        <div className="text-center mb-16" data-id="element-246">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6" data-id="element-247">
            Everything Inside "Is This God or My Fear?"
          </h2>
          <p className="text-xl text-charcoal/80 font-serif italic" data-id="element-248">
            Relationship Edition — A Complete Toolkit, Not Just a Book
          </p>
          <p className="mt-4 text-lg" data-id="element-249">
            When you download your copy today, you get:
          </p>
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            GROUP 1: Core Framework (hero card)
           ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white p-8 md:p-10 rounded-2xl shadow-md border border-gold/20 mb-8 relative overflow-hidden" data-id="element-250">
          <div className="absolute top-0 left-0 w-full h-1 bg-gold" data-id="element-251"></div>
          <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-252">
            <div className="text-4xl" data-id="element-253">📌</div>
            <div data-id="element-254">
              <span className="text-gold font-semibold text-xs tracking-widest uppercase" data-id="element-255">
                Included
              </span>
              <h4 className="font-bold text-xl md:text-2xl mb-3 text-charcoal mt-1" data-id="element-256">
                The 3-Filter Discernment Framework
              </h4>
              <p className="text-charcoal/80 leading-relaxed" data-id="element-257">
                Body + Fruit + Timing explained step-by-step with real
                relationship scenarios. Not theory. Not theology. A system you
                can use tonight.
              </p>
            </div>
          </div>
        </motion.div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            GROUP 2: Pocket Card & Scorecard (cards + image)
           ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-258">
          <ToolCard icon="🃏" title="TOOL #1: The Relationship Pocket Card" desc='"Three questions. Five minutes. One clear next step." Designed to live on your phone wallpaper, in your wallet, or on a sticky note by your bed.' delay={0.05} data-id="element-259" />
          <ToolCard icon="📊" title="TOOL #2: The 2-Minute Decision Scorecard" desc="Rate your decision across 10 dimensions — Body, Fruit, Timing, Peace, and Clarity. Add up your score and instantly know: Green, Yellow, or Red." delay={0.1} data-id="element-260" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-8 bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-warm-gray" data-id="element-261">
          <img src="https://cdn.magicpatterns.com/uploads/wMJWJYhMufmBqeMDopWcgB/Tool_1_-_2_-_The_Pocket_Card__Scorecard.png" alt="The Pocket Card on phone and Decision Scorecard printed — digital tools you can use anywhere" className="w-full rounded-xl" data-id="element-262" />
          <p className="text-center text-sm text-charcoal/50 mt-4 font-serif italic" data-id="element-263">
            Digital but physically useful — on your phone, in your wallet, or by
            your bed.
          </p>
        </motion.div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            GROUP 3: Decision Sheet & Fruit Table (paired)
           ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-264">
          <ToolCard icon="🚦" title="TOOL #3: Green / Yellow / Red Decision Sheet" desc="A fillable timing check for when you know what you want to do but aren't sure when. Maps your decision across 6 dimensions and gives you a clear color." delay={0.05} data-id="element-265" />
          <ToolCard icon="📋" title="TOOL #4: Yes vs. No Fruit Table" desc="Complete both columns for your specific relationship decision. Be honest — not hopeful, not fearful. See the likely 6-month fruit of each path side-by-side." delay={0.1} data-id="element-266" />
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            GROUP 4: Featured tools with images (alternating)
           ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div className="space-y-8 mb-8" data-id="element-267">
          <FeaturedTool icon="💬" title="TOOL #5: 7 Word-for-Word Relationship Scripts" desc="Say exactly this. Starting a hard conversation, asking for space, naming a pattern without blame, expressing a need directly, responding to pressure, ending a conversation with care, saying a hard truth with love." tagline="No more freezing. No more rehearsing for hours. Just the words." imageSrc="https://cdn.magicpatterns.com/uploads/oUJVDgXmf3LYtESMpCaaQY/Tool_-_5_-_7_Word-for-Word_Scripts.png" imageAlt="Phone showing a word-for-word relationship script ready to use in a real conversation" data-id="element-268" />
          <FeaturedTool icon="📅" title="TOOL #6: The 7-Day Discernment Reset" desc="One practice per day. Seven days of clarity. Built specifically for relationship decisions — with a filter focus, a practice, and a prayer for each day. Turn discernment from an emergency response into a daily habit." tagline="This is what peace looks like on the other side of a decision." imageSrc="https://cdn.magicpatterns.com/uploads/ktCuGoinfqnpEdF2fR5AUZ/The_7-Day_Discernment_Reset.png" imageAlt="Woman sitting peacefully on a couch with a cup of tea and the Is This God or My Fear guide" imageLeft data-id="element-269" />
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            GROUP 5: Journal Prompts, Plan & Bonuses
           ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-270">
          <ToolCard icon="📝" title="TOOL #7: 30 Journal Prompts" desc="Categorized by filter and decision stage: Body (6), Fruit (6), Timing (6), Peace vs. Attachment (6), Clarity & Next Steps (6)." delay={0.05} data-id="element-271" />
          <ToolCard icon="🗺️" title="TOOL #8: Personal Discernment Plan" desc="Your 1-page commitment sheet. Fill it out once. Return to it monthly. Tracks your decision, your filter results, your Witness conversation, and your next step." delay={0.1} data-id="element-272" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gold/10 mb-12" data-id="element-273">
          <div className="flex flex-col md:flex-row md:items-start gap-5" data-id="element-274">
            <div className="text-3xl" data-id="element-275">🎁</div>
            <div data-id="element-276">
              <h4 className="font-bold text-lg mb-2 text-charcoal" data-id="element-277">
                3 BONUSES INCLUDED
              </h4>
              <ul className="text-charcoal/70 text-sm leading-relaxed space-y-2" data-id="element-278">
                <li data-id="element-279">
                  <span className="font-semibold text-charcoal" data-id="element-280">
                    The 72-Hour Rule
                  </span>{' '}
                  — A structured waiting period with a built-in revisit so
                  Yellow never becomes avoidance.
                </li>
                <li data-id="element-281">
                  <span className="font-semibold text-charcoal" data-id="element-282">
                    The Witness Conversation Script
                  </span>{' '}
                  — One trusted person. One honest conversation. Five steps for
                  outside perspective.
                </li>
                <li data-id="element-283">
                  <span className="font-semibold text-charcoal" data-id="element-284">
                    The Emergency Filter
                  </span>{' '}
                  — 4 questions. 90 seconds. For when a decision can't wait and
                  you need clarity right now.
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            PRICING & CLOSE
           ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}

        {/* Pricing Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-2xl shadow-lg border border-gold/20 overflow-hidden mb-10" data-id="element-285">
          <div className="bg-gold/10 py-6 text-center border-b border-gold/20" data-id="element-286">
            <h3 className="text-2xl font-bold font-serif text-charcoal" data-id="element-287">
              What Is All Of This Worth?
            </h3>
          </div>
          <div className="p-6 md:p-8" data-id="element-288">
            <div className="space-y-4 mb-8" data-id="element-289">
              {[['3-Filter Framework', '$47'], ['Pocket Card + Scorecard', '$27'], ['7 Scripts', '$37'], ['7-Day Reset', '$47'], ['30 Journal Prompts', '$17'], ['Personal Discernment Plan', '$27'], ['72-Hour Rule + Emergency Filter', '$37']].map(([name, price], i) => <div key={i} className="flex justify-between items-center border-b border-warm-gray pb-4" data-id="element-290">
                  <span className="font-medium" data-id="element-291">{name}</span>
                  <span className="text-charcoal/60" data-id="element-292">{price}</span>
                </div>)}
            </div>

            <div className="flex justify-between items-center mb-6 text-lg" data-id="element-293">
              <span className="font-bold text-charcoal/60" data-id="element-294">
                Total Real Value
              </span>
              <span className="font-bold text-charcoal/60 line-through" data-id="element-295">
                $239
              </span>
            </div>

            <div className="flex justify-between items-center bg-gold/5 p-4 rounded-xl border border-gold/20" data-id="element-296">
              <span className="font-bold text-xl md:text-2xl text-charcoal" data-id="element-297">
                Your Price Today
              </span>
              <span className="font-bold text-3xl md:text-4xl text-gold" data-id="element-298">
                $17.95
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bundle Image */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-10" data-id="element-299">
          <img src="https://cdn.magicpatterns.com/uploads/6bbrXBCEyVQ5UFjSoLJ8qs/VALUE_STACK.png" alt="The complete Is This God or My Fear toolkit — book, tablet, phone with Pocket Card, Decision Scorecard, Fruit Table, and all printable tools spread out" className="w-full max-w-3xl mx-auto rounded-2xl" data-id="element-300" />
          <p className="text-center text-charcoal/90 font-serif italic text-lg mt-6" data-id="element-301">
            All of this — for less than the cost of a single coffee date.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} id="pricing" data-id="element-302">
          <CTAButton mainText="YES — I WANT CLARITY FOR $17.95" subText="Instant PDF Download. Use it tonight." large isCheckout data-id="element-303" />
        </motion.div>
      </div>
    </section>;
}